import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { parseICSContent, fetchICSFromUrl } from '../utils/icsParser';
import { track } from '../utils/analytics';

export interface ImportedCalendar {
  id: string;
  name: string;
  url: string;
  color: string;
  enabled: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
}

export interface ImportedEvent {
  id: string;
  calendar_id: string;
  external_uid: string;
  title: string;
  description?: string;
  dates: string[];
  all_day: boolean;
  start_time?: string;
  end_time?: string;
  location?: string;
}

export function useImportedCalendars() {
  const { user } = useAuth();
  const [calendars, setCalendars] = useState<ImportedCalendar[]>([]);
  const [events, setEvents] = useState<ImportedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);

  // Calendar visibility — local-only, persisted to localStorage
  const [hiddenCalendarIds, setHiddenCalendarIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('mozayc-hidden-calendars');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  // Fetch calendars
  const fetchCalendars = useCallback(async () => {
    if (!user) {
      setCalendars([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('imported_calendars')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCalendars(data || []);
    } catch (err) {
      console.error('Error fetching calendars:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch all imported events
  const fetchEvents = useCallback(async () => {
    if (!user) {
      setEvents([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('imported_events')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setEvents(data || []);
    } catch (err) {
      console.error('Error fetching imported events:', err);
    }
  }, [user]);

  // Add calendar
  const addCalendar = async (name: string, url: string, color: string): Promise<ImportedCalendar | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('imported_calendars')
        .insert([{ 
          user_id: user.id,
          name,
          url,
          color,
        }])
        .select()
        .single();

      if (error) throw error;

      setCalendars(prev => [...prev, data]);
      track('calendar_imported', { source: 'ics' });

      // Sync in the background so the calendar appears instantly — events stream in
      // when the fetch completes; syncCalendar self-contains its errors and sync state.
      if (data) {
        void syncCalendar(data as ImportedCalendar);
      }

      return data;
    } catch (err) {
      console.error('Error adding calendar:', err);
      return null;
    }
  };

  // Update calendar
  const updateCalendar = async (id: string, updates: Partial<ImportedCalendar>) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('imported_calendars')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setCalendars(prev => prev.map(c => c.id === id ? data : c));
      return data;
    } catch (err) {
      console.error('Error updating calendar:', err);
    }
  };

  // Delete calendar (cascade deletes events)
  const deleteCalendar = async (id: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('imported_calendars')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setCalendars(prev => prev.filter(c => c.id !== id));
      setEvents(prev => prev.filter(e => e.calendar_id !== id));
    } catch (err) {
      console.error('Error deleting calendar:', err);
    }
  };

  // Toggle calendar visibility (local only)
  const toggleCalendar = useCallback((id: string) => {
    setHiddenCalendarIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem('mozayc-hidden-calendars', JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Sync a single calendar
  // Can pass either an id (to look up from state) or the full calendar object
  const syncCalendar = async (idOrCalendar: string | ImportedCalendar) => {
    if (!user) return;

    let calendar: ImportedCalendar | undefined;
    let id: string;

    if (typeof idOrCalendar === 'string') {
      id = idOrCalendar;
      calendar = calendars.find(c => c.id === id);
      if (!calendar) {
        console.error('Calendar not found for sync:', id);
        return;
      }
    } else {
      calendar = idOrCalendar;
      id = calendar.id;
    }

    // Skip file-imported calendars — they don't sync
    if (calendar.url === 'file://imported') return;

    setSyncing(id);

    try {
      // Fetch ICS content
      const icsContent = await fetchICSFromUrl(calendar.url);
      
      // Parse events
      const parsedEvents = parseICSContent(icsContent);
      
      // Delete existing events for this calendar
      await supabase
        .from('imported_events')
        .delete()
        .eq('calendar_id', id);

      // Insert new events — deduplicate by UID first (some feeds repeat the same UID)
      if (parsedEvents.length > 0) {
        const seen = new Map<string, typeof parsedEvents[0]>();
        for (const event of parsedEvents) seen.set(event.uid, event);

        const eventsToInsert = Array.from(seen.values()).map(event => ({
          user_id: user.id,
          calendar_id: id,
          external_uid: event.uid,
          title: event.title,
          description: event.description,
          dates: event.dates,
          all_day: event.allDay,
          start_time: event.startTime,
          end_time: event.endTime,
          location: event.location,
        }));

        const { error: insertError } = await supabase
          .from('imported_events')
          .upsert(eventsToInsert, { onConflict: 'calendar_id,external_uid' });

        if (insertError) throw insertError;
      }

      // Update sync timestamp
      await supabase
        .from('imported_calendars')
        .update({ 
          last_synced_at: new Date().toISOString(),
          sync_error: null 
        })
        .eq('id', id);

      // Refresh data
      await fetchCalendars();
      await fetchEvents();

    } catch (err) {
      console.error('Error syncing calendar:', err);
      
      await supabase
        .from('imported_calendars')
        .update({ 
          sync_error: err instanceof Error ? err.message : 'Sync failed'
        })
        .eq('id', id);

      await fetchCalendars();
    } finally {
      setSyncing(null);
    }
  };

  // Sync all calendars
  const syncAllCalendars = async () => {
    for (const calendar of calendars) {
      await syncCalendar(calendar.id);
    }
  };

  // Import calendar from file content (for iCloud and other calendars that don't work with CORS proxies)
  const importFromFile = async (name: string, icsContent: string, color: string): Promise<ImportedCalendar | null> => {
    if (!user) return null;

    try {
      // Create calendar entry (without URL since it's file-based)
      const { data, error } = await supabase
        .from('imported_calendars')
        .insert([{
          user_id: user.id,
          name,
          url: 'file://imported', // Marker to indicate file import
          color,
          last_synced_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;

      // Parse the ICS content
      const parsedEvents = parseICSContent(icsContent);

      // Insert events
      if (parsedEvents.length > 0) {
        const eventsToInsert = parsedEvents.map(event => ({
          user_id: user.id,
          calendar_id: data.id,
          external_uid: event.uid,
          title: event.title,
          description: event.description,
          dates: event.dates,
          all_day: event.allDay,
          start_time: event.startTime,
          end_time: event.endTime,
          location: event.location,
        }));

        const { error: insertError } = await supabase
          .from('imported_events')
          .insert(eventsToInsert);

        if (insertError) throw insertError;
      }

      setCalendars(prev => [...prev, data]);
      await fetchEvents();

      return data;
    } catch (err) {
      console.error('Error importing calendar from file:', err);
      return null;
    }
  };

  // Optimistic: remove event from state immediately, DB in background
  const deleteImportedEvent = async (eventId: string | number) => {
    let removedEvent: ImportedEvent | undefined;
    setEvents(prev => {
      removedEvent = prev.find(e => e.id === eventId);
      return prev.filter(e => e.id !== eventId);
    });

    try {
      const { error } = await supabase.from('imported_events').delete().eq('id', eventId);
      if (error) throw error;
    } catch (err) {
      // Rollback
      if (removedEvent) setEvents(prev => [...prev, removedEvent!]);
      console.error('Error deleting imported event:', err);
    }
  };

  // Update an imported event (optimistic + DB)
  const updateImportedEvent = useCallback(async (eventId: string | number, updates: Partial<ImportedEvent>) => {
    let original: ImportedEvent | undefined;
    setEvents(prev => prev.map(e => {
      if (e.id === eventId) {
        original = e;
        return { ...e, ...updates };
      }
      return e;
    }));

    try {
      const { error } = await supabase
        .from('imported_events')
        .update({
          title: updates.title,
          description: updates.description,
          dates: updates.dates,
          all_day: updates.all_day,
          start_time: updates.start_time,
          end_time: updates.end_time,
        })
        .eq('id', eventId);
      if (error) throw error;
    } catch (err) {
      if (original) setEvents(prev => prev.map(e => e.id === eventId ? original! : e));
      console.error('Error updating imported event:', err);
    }
  }, []);

  // Optimistic: add event to local state instantly (no DB call)
  const addOptimisticEvent = useCallback((event: ImportedEvent) => {
    setEvents(prev => [...prev, event]);
  }, []);

  // Optimistic: update an event in local state by matching a field
  const updateOptimisticEvent = useCallback((match: { external_uid: string }, updates: Partial<ImportedEvent>) => {
    setEvents(prev => prev.map(e =>
      e.external_uid === match.external_uid ? { ...e, ...updates } : e
    ));
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  // Fetch events when calendars change
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Realtime: refetch when imported_calendars rows change (e.g. added from Settings)
  useEffect(() => {
    if (!user) return;
    const sub = supabase
      .channel(`imported_calendars:${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'imported_calendars', filter: `user_id=eq.${user.id}` },
        () => { fetchCalendars(); fetchEvents(); }
      )
      .subscribe();
    return () => { sub.unsubscribe(); };
  }, [user, fetchCalendars, fetchEvents]);

  // Derive enabled from local visibility state
  const visibleCalendars = calendars.map(c => ({
    ...c,
    enabled: !hiddenCalendarIds.has(c.id),
  }));

  // Filter events to only visible calendars
  const visibleEvents = events.filter(e => !hiddenCalendarIds.has(e.calendar_id));

  return {
    calendars: visibleCalendars,
    allEvents: events,
    events: visibleEvents,
    loading,
    syncing,
    addCalendar,
    updateCalendar,
    deleteCalendar,
    toggleCalendar,
    syncCalendar,
    syncAllCalendars,
    importFromFile,
    refetch: fetchCalendars,
    refetchEvents: fetchEvents,
    deleteImportedEvent,
    updateImportedEvent,
    addOptimisticEvent,
    updateOptimisticEvent,
  };
}